type AgentModel = {
  providerID: string
  modelID: string
}

type Agent = {
  model?: AgentModel
  variant?: string
}

type Model = AgentModel & {
  variants?: Record<string, unknown>
}

type VariantInput = {
  variants: string[]
  selected: string | null | undefined
  configured: string | undefined
}

function normalizeVariant(value: string) {
  const key = value.trim().replace(/[\s-]+/g, "_").toLowerCase()
  if (key === "extrahigh" || key === "extra_high" || key === "xhigh" || key === "x_high") return "extra_high"
  if (key === "maximum") return "max"
  return key
}

function resolveVariantAlias(value: string | undefined, variants: string[]) {
  if (!value) return undefined
  if (variants.includes(value)) return value

  const normalized = normalizeVariant(value)
  if (variants.includes(normalized)) return normalized
  if (normalized === "extra_high" && variants.includes("xhigh")) return "xhigh"
  if (normalized === "max" && variants.includes("extra_high")) return "extra_high"
  return undefined
}

export function getConfiguredAgentVariant(input: { agent: Agent | undefined; model: Model | undefined }) {
  if (!input.agent?.variant) return undefined
  if (!input.agent.model) return undefined
  if (!input.model?.variants) return undefined
  if (input.agent.model.providerID !== input.model.providerID) return undefined
  if (input.agent.model.modelID !== input.model.modelID) return undefined
  return resolveVariantAlias(input.agent.variant, Object.keys(input.model.variants))
}

export function resolveModelVariant(input: VariantInput) {
  if (input.selected === null) return undefined
  const selected = resolveVariantAlias(input.selected ?? undefined, input.variants)
  if (selected) return selected
  const configured = resolveVariantAlias(input.configured, input.variants)
  if (configured) return configured
  return undefined
}

// When the current scope has not explicitly chosen a reasoning variant
// (`undefined`), fall back to the persisted last-used variant so it carries
// into new sessions. An explicit `null` (the user cleared reasoning for this
// scope) or an explicit value is always kept.
export function selectedVariantWithPreference(
  scopeVariant: string | null | undefined,
  lastVariant: string | null | undefined,
): string | null | undefined {
  return scopeVariant === undefined ? lastVariant : scopeVariant
}

export function cycleModelVariant(input: VariantInput) {
  if (input.variants.length === 0) return undefined
  if (input.selected === null) return input.variants[0]
  const selected = resolveVariantAlias(input.selected ?? undefined, input.variants)
  if (selected) {
    const index = input.variants.indexOf(selected)
    if (index === input.variants.length - 1) return undefined
    return input.variants[index + 1]
  }
  const configured = resolveVariantAlias(input.configured, input.variants)
  if (configured) {
    const index = input.variants.indexOf(configured)
    if (index === input.variants.length - 1) return input.variants[0]
    return input.variants[index + 1]
  }
  return input.variants[0]
}
